// Visibilidad, envío gratis e "Instagram y Google Shopping" (MPN, rango de edad, género), como en la
// ficha de producto del panel de Tiendanube (ADR-2026-10-29-visibilidad-envio-y-google-shopping). Puro.
//
// Visibilidad y envío son del producto; los de Google son de cada variante en Tiendanube, y la ficha
// los maneja como uno solo para todas (como el panel). En la ficha, null = "no definido": un producto
// nuevo no los manda, uno de la tienda arranca con lo que tiene allá.

import type { CambioDeCampo, CambiosParaLaTienda, ProductoEnLaTienda } from "./cambiosContraLaTienda";
import type { SeccionTrabable } from "./seccionesTrabadas";

export type Visibilidad = "visible" | "unlisted" | "hidden";
export type RangoDeEdad = "newborn" | "infant" | "toddler" | "kids" | "adult";
export type Genero = "female" | "male" | "unisex";

export const VISIBILIDADES: ReadonlyArray<{ id: Visibilidad; texto: string; ayuda: string }> = [
  { id: "visible", texto: "Visible", ayuda: "Aparece en la tienda, en buscadores y en los canales de venta conectados." },
  { id: "unlisted", texto: "No listado", ayuda: "No aparece en la tienda ni en buscadores, pero se puede comprar con el link." },
  { id: "hidden", texto: "Oculto", ayuda: "No se muestra ni se puede comprar. El link no funciona." },
];

export const RANGOS_DE_EDAD: ReadonlyArray<{ id: RangoDeEdad; texto: string }> = [
  { id: "newborn", texto: "Recién nacidos" },
  { id: "infant", texto: "Bebés" },
  { id: "toddler", texto: "Niños pequeños" },
  { id: "kids", texto: "Niños" },
  { id: "adult", texto: "Adultos" },
];

export const GENEROS: ReadonlyArray<{ id: Genero; texto: string }> = [
  { id: "female", texto: "Femenino" },
  { id: "male", texto: "Masculino" },
  { id: "unisex", texto: "Unisex" },
];

const textoDe = <T extends string>(lista: ReadonlyArray<{ id: T; texto: string }>, id: T | null | undefined) =>
  lista.find((x) => x.id === id)?.texto ?? "—";

/** Los cinco campos, como los guarda la ficha. */
export interface CamposDeLaTiendaEnLaFicha {
  visibilidad: Visibilidad | null;
  envioGratis: boolean | null;
  /** En los tres de Google, "" es "vaciarlo en la tienda"; null, "lo que tenga la tienda". */
  mpn: string | null;
  rangoEdad: RangoDeEdad | "" | null;
  genero: Genero | "" | null;
}

/** Los cinco, con los nombres del pedido (`nuevo` y `cambios` del export). */
export type CamposDeLaTiendaParaElPedido = Pick<CambiosParaLaTienda, "visibilidad" | "envio_gratis" | "mpn" | "rango_edad" | "genero">;

/** Lo que va al crear el producto (`nuevo` del export): sólo lo definido. */
export function camposParaCrear(p: Partial<CamposDeLaTiendaEnLaFicha>): CamposDeLaTiendaParaElPedido {
  return {
    ...(p.visibilidad ? { visibilidad: p.visibilidad } : {}),
    ...(p.envioGratis != null ? { envio_gratis: p.envioGratis } : {}),
    ...(p.mpn?.trim() ? { mpn: p.mpn.trim() } : {}),
    ...(p.rangoEdad ? { rango_edad: p.rangoEdad } : {}),
    ...(p.genero ? { genero: p.genero } : {}),
  };
}

/** La ficha de un producto de la tienda arranca con esto (`fichaDesdeLaTienda`). */
export function camposDesdeLaTienda(e: ProductoEnLaTienda): CamposDeLaTiendaEnLaFicha {
  return {
    visibilidad: e.visibilidad ?? (e.publicado ? "visible" : "hidden"),
    envioGratis: e.envio_gratis ?? false,
    mpn: e.mpn ?? null,
    rangoEdad: e.rango_edad ?? null,
    genero: e.genero ?? null,
  };
}

/**
 * Compara los cinco campos con la tienda y los anota como cambios (con el mismo criterio que el resto:
 * cambia si la ficha es distinta de la tienda y de con qué arrancó). Un campo que la ficha no tiene
 * (null) o que la lectura de la tienda no trae (bases viejas) no se compara.
 */
export function compararCamposDeLaTienda(
  p: Partial<CamposDeLaTiendaEnLaFicha>,
  e: ProductoEnLaTienda,
  base: ProductoEnLaTienda | null,
  registrar: (c: CambioDeCampo, aplicar: () => void) => void,
  pedido: CambiosParaLaTienda,
): void {
  const tienda = camposDesdeLaTienda(e);
  const deBase = base ? camposDesdeLaTienda(base) : null;
  const anotar = <K extends keyof CamposDeLaTiendaEnLaFicha>(
    campo: K,
    seccion: SeccionTrabable,
    etiqueta: string,
    ver: (v: CamposDeLaTiendaEnLaFicha[K] | null | undefined) => string,
    alPedido: () => void,
  ) => {
    const ficha = p[campo];
    if (ficha === null || ficha === undefined) return;
    const igual = (a: unknown, b: unknown) => (a ?? null) === (b ?? null) || (typeof a === "string" && typeof b === "string" && a.trim() === b.trim());
    if (igual(ficha, tienda[campo])) return;
    if (deBase && igual(ficha, deBase[campo])) return;
    registrar(
      {
        campo,
        etiqueta,
        seccion,
        antes: ver(tienda[campo]),
        ahora: ver(ficha),
        // Sin "Deshacer" desde el campo: no son textos (se vuelve con "Traer lo de la tienda").
      },
      alPedido,
    );
  };
  // Las bases de antes no traen estos campos: sin el dato de la tienda no hay con qué comparar.
  if (e.visibilidad !== undefined || e.publicado !== undefined) {
    anotar("visibilidad", "visibilidad", "Visibilidad", (v) => textoDe(VISIBILIDADES, v as Visibilidad), () => {
      pedido.visibilidad = p.visibilidad!;
    });
  }
  if (e.envio_gratis !== undefined) {
    anotar("envioGratis", "envioGratis", "Envío gratis", (v) => (v ? "Sí" : "No"), () => {
      pedido.envio_gratis = p.envioGratis!;
    });
  }
  if (e.mpn !== undefined) {
    anotar("mpn", "google", "MPN", (v) => (v ? String(v) : "—"), () => {
      pedido.mpn = (p.mpn ?? "").trim();
    });
  }
  if (e.rango_edad !== undefined) {
    anotar("rangoEdad", "google", "Rango de edad", (v) => textoDe(RANGOS_DE_EDAD, v as RangoDeEdad), () => {
      pedido.rango_edad = p.rangoEdad ?? "";
    });
  }
  if (e.genero !== undefined) {
    anotar("genero", "google", "Género", (v) => textoDe(GENEROS, v as Genero), () => {
      pedido.genero = p.genero ?? "";
    });
  }
}
